import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { Pill } from "@phosphor-icons/react"
import { joinStrings } from "@/lib/joinString"
import { RequestedMedicationsFragment$key } from "@/modules/patients/services/__generated__/RequestedMedicationsFragment.graphql"
import { RequestedMedicationsFragment } from "@/modules/patients/services/RequestedMedicationsFragment"

interface RequestedMedicationsSummaryProps {
  appointmentRef: RequestedMedicationsFragment$key
}

const RequestedMedicationsSummary = ({ appointmentRef }: RequestedMedicationsSummaryProps) => {
  const { t } = useTranslation()
  const { requestedMedications } = useFragment<RequestedMedicationsFragment$key>(
    RequestedMedicationsFragment,
    appointmentRef,
  )

  if (!requestedMedications?.length) {
    return null
  }

  return (
    <div className="bg-surface-subdued rounded-md p-3" data-testid="requested-medications-summary">
      <span className="text-on-surface-subdued mb-2 block text-xs font-medium">
        {t("episodeOfCare.progressNote.requestedMedications.title")}
      </span>
      <ul className="flex flex-col gap-2">
        {requestedMedications.map((requestedMedication) => (
          <li
            key={`${requestedMedication.name}-${requestedMedication.mainSubstance ?? ""}-${requestedMedication.packageSize ?? ""}-${requestedMedication.pharmaCode ?? ""}`}
            className="border-border bg-surface flex items-start gap-2 rounded-md border p-3">
            <Pill className="text-on-surface-subdued mt-0.5 h-4 w-4 shrink-0" />
            <span className="flex flex-col">
              <span className="text-on-surface text-sm wrap-break-word">
                {joinStrings([requestedMedication.name, requestedMedication.mainSubstance])}
              </span>
              {requestedMedication.packageSize && (
                <span className="text-on-surface-subdued text-xs">{requestedMedication.packageSize}</span>
              )}
              {requestedMedication.productUrl && (
                <a
                  className="text-primary mt-1 w-fit text-sm underline"
                  href={requestedMedication.productUrl}
                  target="_blank"
                  rel="noopener noreferrer">
                  {t("patient.medication.compendium")}
                </a>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export { RequestedMedicationsSummary }
